import { Component, Injector, OnInit } from '@angular/core';
import { FormControl, FormGroup, AbstractControl } from '@angular/forms';
import { ApAlertService} from 'webcommon/core-ui';
import {
  FcModifyScheduledReportItemDto,
  FcScheduledReportItemDto,
  FindControlRequiredSearchParametersDto,
  FindControlAdditionalSearchParametersDto,
  IColumnPk,
  FindControlAdditionalSearchParametersType,
} from 'webcommon/shared';
import { FcGenericItemModalBaseComponent } from '../../generic/fc-generic-item-modal-base.component';
import { FcListValidationService } from '../../generic/fc-list-validation/fc-list-validation.service';
import { DatePipe} from '@angular/common';
import { FcItemModalMode } from '../../../models';

@Component({
  providers: [ApAlertService, FcListValidationService, DatePipe],
  selector: 'fc-scheduledreport-modal',
  templateUrl: './fc-scheduledreport-modal.component.html',
})
export class FcScheduledReportListModalComponent extends FcGenericItemModalBaseComponent implements OnInit {

  public override baseId = 'fc-scheduledreport-modal';

  public uiFilterTypeRequiredSearchParameters = {
    Type: FindControlRequiredSearchParametersDto,
    RequiredSearchParameters: [
        {
            ColumnName: 'UIFilterTypeUid',
            Negate: false,
            Value: this.systemGuid.UIFilterTypeReports,
        },
    ],
  };

  public uiFilterTypeAdditionalSearchParameters: FindControlAdditionalSearchParametersDto = {
    Type: FindControlAdditionalSearchParametersType.Standard,
    AdditionalSearchParameters: []
  };

  public disableFilter = true;
  private _selectedReportUid: string[] = [];
  public get selectedReportUid(): string[] { return this._selectedReportUid; }
  public set selectedReportUid(val: string[]) {
    if (!val) { val = []; }
    if (!this.arraysEqual(this._selectedReportUid, val)) {
      this._selectedReportUid = [...val];
      // Keep form control in sync
      const expected = this.reportControl.value ? [this.reportControl.value] : [];
      if (!this.arraysEqual(expected, this._selectedReportUid)) {
        // Single select; first element maps to control value
        const first = this._selectedReportUid[0] || null;
        this.reportControl.setValue(first, { emitEvent: false });
        this.reportControl.updateValueAndValidity({ emitEvent: false });
      }
      if (!this.isInitializing) { this.markDirty(this.reportControl); }
    }
  }

  private _selectedUiFilterUid: string[] = [];
  public get selectedUiFilterUid(): string[] { return this._selectedUiFilterUid; }
  public set selectedUiFilterUid(val: string[]) {
    if (!val) { val = []; }
    if (!this.arraysEqual(this._selectedUiFilterUid, val)) {
      this._selectedUiFilterUid = [...val];
      const first = this._selectedUiFilterUid[0] || null;
      this.uiFilterControl.setValue(first, { emitEvent: false });
      this.uiFilterControl.updateValueAndValidity({ emitEvent: false });
      if (!this.isInitializing) { this.markDirty(this.uiFilterControl); }
    }
  }

  private _selectedJobScheduleTimeBaseUid: string[] = [];
  public get selectedJobScheduleTimeBaseUid(): string[] { return this._selectedJobScheduleTimeBaseUid; }
  public set selectedJobScheduleTimeBaseUid(val: string[]) {
    if (!val) { val = []; }
    if (!this.arraysEqual(this._selectedJobScheduleTimeBaseUid, val)) {
      const prevFirst = this._selectedJobScheduleTimeBaseUid[0];
      this._selectedJobScheduleTimeBaseUid = [...val];
      const first = this._selectedJobScheduleTimeBaseUid[0] || null;
      if (first && prevFirst && first !== prevFirst) {
        this.resetRepeatIntervalControls();
      }
      if (first) {
        this.jobScheduleTimeBaseControl.setValue(first, { emitEvent: false });
      } else {
        // Clear control value when selection emptied
        this.jobScheduleTimeBaseControl.setValue(null, { emitEvent: false });
      }
      if (!this.isInitializing && prevFirst !== first) {
        this.markDirty(this.jobScheduleTimeBaseControl);
      }
      // Re-run validation in case required is violated
      this.jobScheduleTimeBaseControl.updateValueAndValidity({ emitEvent: false });
    }
  }
  private _selectedViewPersonUid: string[] = [];
  public get selectedViewPersonUid(): string[] { return this._selectedViewPersonUid; }
  public set selectedViewPersonUid(val: string[]) {
    // Called via two-way [(selectedPks)] binding from recipients find control
    if (!val) { val = []; }
    if (!this.arraysEqual(this._selectedViewPersonUid, val)) {
      this._selectedViewPersonUid = [...val];
      // Sync reactive form control but avoid triggering valueChanges loop
      this.viewPersonControl.setValue(this._selectedViewPersonUid, { emitEvent: false });
      if (!this.isInitializing) {
        this.markDirty(this.viewPersonControl);
      }
    }
  }
  public showNotes = true;
  public showJobSuccessHistoryDepth = true;
  private isInitializing = false;


  public listItem: FcScheduledReportItemDto =
  {
      Id: '',
      ExternalId: '',
      Name: '',
      Notes: '',
      JobUid: this.systemGuid.JobScheduleContextJobUidScheduledReportsAngular,
      Inactive: false,
      JobSuccessHistoryDepth: '0',
      JobScheduleTimeBaseUid: this.systemGuid.JobScheduleTimeBaseRunOnceUid,
      JobRepeatFrom: '',
      JobRepeatInterval: '',
      MethodParams: '',
      MethodParamsReportUid: '',
      MethodParamsUiFilterUid: '',
      MethodParamsRecipientUids: [],
      MethodParamsOutputFormType: '',
      RunImmediately: false,
      UserDefined: true
  };

  // Form Controls
  public externalIdControl = new FormControl();
  public nameControl = new FormControl();
  public notesControl = new FormControl();
  public jobControl = new FormControl();
  public inactiveControl = new FormControl();
  public jobSuccessHistoryDepthControl = new FormControl();
  public jobScheduleTimeBaseControl = new FormControl();
  public uiFilterControl = new FormControl();
  public reportControl = new FormControl();
  public jobRepeatFromControl = new FormControl();
  public startTimeControl = new FormControl();
  public jobRepeatIntervalControl = new FormControl();
  public methodParamsControl = new FormControl();
  public userDefinedControl = new FormControl();
  public viewPersonControl = new FormControl();
  public outputFormatTypeControl = new FormControl();
  public timeBaseNthSecondControl = new FormControl();
  public timeBaseNthMinuteControl = new FormControl();
  public timeBaseHourControl = new FormControl();
  public timeBaseDailyTypeControl = new FormControl();
  public timeBaseDailyTimeControl = new FormControl();
  public timeBaseWeeklyTimeControl = new FormControl();
  public timeBaseWeeklySundayControl = new FormControl();
  public timeBaseWeeklyMondayControl = new FormControl();
  public timeBaseWeeklyTuesdayControl = new FormControl();
  public timeBaseWeeklyWednesdayControl = new FormControl();
  public timeBaseWeeklyThursdayControl = new FormControl();
  public timeBaseWeeklyFridayControl = new FormControl();
  public timeBaseWeeklySaturdayControl = new FormControl();
  public timeBaseMonthlyDayOfMonthControl = new FormControl();
  public timeBaseMonthlyTimeControl = new FormControl();
  public runImmediatelyControl = new FormControl();

  public form: FormGroup = new FormGroup({
    externalId: this.externalIdControl,
    name: this.nameControl,
    notes: this.notesControl,
    jobUid: this.jobControl,
    reportUid: this.reportControl,
    uiFilterUid: this.uiFilterControl,
    inactive: this.inactiveControl,
    jobSuccessHistoryDepth: this.jobSuccessHistoryDepthControl,
    jobScheduleTimeBaseUid: this.jobScheduleTimeBaseControl,
    jobRepeatFrom: this.jobRepeatFromControl,
    jobRepeatInterval: this.jobRepeatIntervalControl,
    methodParams: this.methodParamsControl,
    userDefined: this.userDefinedControl,
    startTime: this.startTimeControl,
    viewPerson: this.viewPersonControl,
    outputFormatType: this.outputFormatTypeControl,
    timeBaseNthSecond: this.timeBaseNthSecondControl,
    timeBaseNthMinute: this.timeBaseNthMinuteControl,
    timeBaseHour: this.timeBaseHourControl,
    timeBaseDailyType: this.timeBaseDailyTypeControl,
    timeBaseDailyTime: this.timeBaseDailyTimeControl,
    timeBaseWeeklyTime: this.timeBaseWeeklyTimeControl,
    timeBaseWeeklySunday: this.timeBaseWeeklySundayControl,
    timeBaseWeeklyMonday: this.timeBaseWeeklyMondayControl,
    timeBaseWeeklyTuesday: this.timeBaseWeeklyTuesdayControl,
    timeBaseWeeklyWednesday: this.timeBaseWeeklyWednesdayControl,
    timeBaseWeeklyThursday: this.timeBaseWeeklyThursdayControl,
    timeBaseWeeklyFriday: this.timeBaseWeeklyFridayControl,
    timeBaseWeeklySaturday: this.timeBaseWeeklySaturdayControl,
    timeBaseMonthlyDayOfMonth: this.timeBaseMonthlyDayOfMonthControl,
    timeBaseMonthlyTime: this.timeBaseMonthlyTimeControl,
    runImmediately: this.runImmediatelyControl,
  });

  constructor(injector: Injector, private datePipe: DatePipe)
  {
      super(injector);
  }

  override ngOnInit() {
    super.ngOnInit();

    this.loadListItem({});
    // Subscribe to value changes for recipients to ensure dirty state even if events don't fire
    this.viewPersonControl.valueChanges.pipe(this.takeUntilDestroy()).subscribe(() => {
      if (!this.isInitializing) {
        this.viewPersonControl.markAsDirty();
        this.form.markAsDirty();
      }
    });

    // Disable weekly day-of-week controls when no weekly time is specified
    this.timeBaseWeeklyTimeControl.valueChanges.pipe(this.takeUntilDestroy()).subscribe(() => {
      this.updateWeeklyDayControlsDisabledState();
    });
  }


  public setForm(): void {
    // Begin initialization sequence
    this.isInitializing = true;

    this.externalIdControl.setValue(this.listItem.ExternalID);

    this.nameControl.setValidators(this.fcListValidation.getValidators('Name'));
    this.nameControl.setValue(this.listItem.Name);
    this.applyRequiredValidator(this.reportControl, 'MethodParamsReportUid');
    this.applyRequiredValidator(this.uiFilterControl, 'MethodParamsUiFilterUid');
    this.notesControl.setValue(this.listItem.Notes);
    this.inactiveControl.setValue(this.listItem.Inactive);
    this.jobSuccessHistoryDepthControl.setValue(this.listItem.JobSuccessHistoryDepth);
    this.jobRepeatFromControl.setValue(this.getDate(this.listItem.JobRepeatFrom || ''));
    this.jobRepeatIntervalControl.setValue(this.listItem.JobRepeatInterval);
    this.startTimeControl.setValidators(this.fcListValidation.getValidators('JobRepeatFrom'));
    this.startTimeControl.setValue(this.getTime(this.listItem.JobRepeatFrom || ''));
    this.methodParamsControl.setValue(this.listItem.MethodParams);
    this.userDefinedControl.disable();
    this.userDefinedControl.setValue(this.listItem.UserDefined);
    this.jobControl.setValue(this.listItem.JobUid);
    const resolvedTimeBase = this.listItem.JobScheduleTimeBaseUid || this.systemGuid.JobScheduleTimeBaseRunOnceUid;
    // Required validator (Perform This Job)
    this.applyRequiredValidator(this.jobScheduleTimeBaseControl, 'JobScheduleTimeBaseUid');
    this.jobScheduleTimeBaseControl.setValue(resolvedTimeBase);
    this.selectedJobScheduleTimeBaseUid = resolvedTimeBase ? [resolvedTimeBase] : [];
    this.timeBaseNthSecondControl.setValue(this.listItem.TimeBaseNthSecond);
    this.timeBaseNthMinuteControl.setValue(this.listItem.TimeBaseNthMinute);
    this.timeBaseHourControl.setValue(this.listItem.TimeBaseHour);
    this.timeBaseDailyTimeControl.setValue(this.getTime(this.listItem.TimeBaseDailyTime || ''));
    const dailyType = (this.listItem.TimeBaseDailyType === undefined || this.listItem.TimeBaseDailyType === null)
      ? 0
      : this.listItem.TimeBaseDailyType;
    this.timeBaseDailyTypeControl.setValue(dailyType);
    this.timeBaseWeeklyTimeControl.setValue(this.getTime(this.listItem.TimeBaseWeeklyTime || ''));
    this.timeBaseWeeklySundayControl.setValue(this.listItem.TimeBaseWeeklySunday);
    this.timeBaseWeeklyMondayControl.setValue(this.listItem.TimeBaseWeeklyMonday);
    this.timeBaseWeeklyTuesdayControl.setValue(this.listItem.TimeBaseWeeklyTuesday);
    this.timeBaseWeeklyWednesdayControl.setValue(this.listItem.TimeBaseWeeklyWednesday);
    this.timeBaseWeeklyThursdayControl.setValue(this.listItem.TimeBaseWeeklyThursday);
    this.timeBaseWeeklyFridayControl.setValue(this.listItem.TimeBaseWeeklyFriday);
    this.timeBaseWeeklySaturdayControl.setValue(this.listItem.TimeBaseWeeklySaturday);
    this.timeBaseMonthlyTimeControl.setValue(this.getTime(this.listItem.TimeBaseMonthlyTime || ''));
    this.timeBaseMonthlyDayOfMonthControl.setValue(this.listItem.TimeBaseMonthlyDayOfMonth);

    // Check for initial report UID from additionalSearchParameters (when modal opened for a specific report)
    let initialReportUid: string | undefined;
    if (this.fcModalItemContext.additionalSearchParameters?.AdditionalSearchParameters) {
      const reportUidParam = this.fcModalItemContext.additionalSearchParameters.AdditionalSearchParameters.find(
        param => param.ColumnName === 'ReportUid'
      );
      if (reportUidParam) {
        initialReportUid = reportUidParam.Value;
      }
    }
    const reportUid = this.listItem.MethodParamsReportUid || initialReportUid;
    if (reportUid) {
      this.reportControl.setValue(reportUid, { emitEvent: false });
      this.selectedReportUid = [reportUid];
      if (this.listItem.MethodParamsUiFilterUid) {
        this.uiFilterControl.setValue(this.listItem.MethodParamsUiFilterUid, { emitEvent: false });
        this.selectedUiFilterUid = [this.listItem.MethodParamsUiFilterUid];
      } else {
        this.uiFilterControl.setValue(null, { emitEvent: false });
        this.selectedUiFilterUid = [];
      }
    } else {
      this.reportControl.setValue(null, { emitEvent: false });
      this.selectedReportUid = [];
      this.uiFilterControl.setValue(null, { emitEvent: false });
      this.selectedUiFilterUid = [];
    }
    this.outputFormatTypeControl.setValue(this.listItem.MethodParamsOutputFormType);
    // Recipients (array) required validator
    this.applyRequiredValidator(this.viewPersonControl, 'MethodParamsRecipientUids', true);
    this.selectedViewPersonUid = this.listItem.MethodParamsRecipientUids;
    this.runImmediatelyControl.setValue(this.listItem.RunImmediately);

    this.methodParamsControl.setValue(this.listItem.MethodParams);

    // Defer to ensure chips in find controls get correct selected arrays after form controls set
    Promise.resolve().then(() => {
      const r = this.reportControl.value; this.selectedReportUid = r ? [r] : [];
      const f = this.uiFilterControl.value; this.selectedUiFilterUid = f ? [f] : [];
      const jb = this.jobScheduleTimeBaseControl.value; this.selectedJobScheduleTimeBaseUid = jb ? [jb] : [];
      // Mark everything pristine so subsequent user edits toggle Save button correctly
      Object.values(this.form.controls).forEach(c => c.markAsPristine());
      this.form.markAsPristine();
      this.validateForm();
      this.isInitializing = false;
      // Ensure initial disabled state for weekly day checkboxes reflects current time value
      this.updateWeeklyDayControlsDisabledState();
    });

  }

  public override save(): void {
    // Defensive: prevent save if form invalid
    if (this.form.invalid) {
      this.isSubmitted = true;
      this.form.markAllAsTouched();
      return;
    }
    const values = this.form.getRawValue();
    const methodParams: string = values.methodParams;

    const modifyItemDto: FcModifyScheduledReportItemDto = {
      ExternalID: values.externalId,
      Name: values.name,
      Notes: values.notes,
      JobUid: values.jobUid,
      Inactive: values.inactive,
      JobSuccessHistoryDepth: values.jobSuccessHistoryDepth,
      JobRepeatFrom: this.setDateTime(values.jobRepeatFrom, values.startTime),
      JobRepeatInterval: values.jobRepeatInterval,
      JobScheduleTimeBaseUid: values.jobScheduleTimeBaseUid,
      TimeBaseNthSecond: values.timeBaseNthSecond,
      TimeBaseNthMinute: values.timeBaseNthMinute,
      TimeBaseHour: values.timeBaseHour,
      TimeBaseDailyTime: values.timeBaseDailyTime,
      TimeBaseDailyType: values.timeBaseDailyType,
      TimeBaseWeeklyTime: values.timeBaseWeeklyTime,
      TimeBaseWeeklySunday: values.timeBaseWeeklySunday,
      TimeBaseWeeklyMonday: values.timeBaseWeeklyMonday,
      TimeBaseWeeklyTuesday: values.timeBaseWeeklyTuesday,
      TimeBaseWeeklyWednesday: values.timeBaseWeeklyWednesday,
      TimeBaseWeeklyThursday: values.timeBaseWeeklyThursday,
      TimeBaseWeeklyFriday: values.timeBaseWeeklyFriday,
      TimeBaseWeeklySaturday: values.timeBaseWeeklySaturday,
      TimeBaseMonthlyTime: values.timeBaseMonthlyTime,
      TimeBaseMonthlyDayOfMonth: values.timeBaseMonthlyDayOfMonth,
      MethodParams: methodParams,
      UserDefined: values.userDefined,
      MethodParamsReportUid: values.reportUid,
      MethodParamsUiFilterUid: values.uiFilterUid,
      MethodParamsOutputFormType: values.outputFormatType,
      MethodParamsRecipientUids: this.selectedViewPersonUid,
      RunImmediately: values.runImmediately
    };

    //super.save<FcModifyScheduledReportItemDto>(modifyItemDto);

    this.isSubmitted = true;
    this.alertService.clearAllAlerts();
    if (!this.form.valid || this.isSaving) {
      return;
    }
    this.isSaving = true;
    if (this.fcModalItemContext.mode === FcItemModalMode.Modify) {
      this.findControlRepository
        .updateItem<FcModifyScheduledReportItemDto>(this.fcModalItemContext.findControlId, this.listItem.Id, modifyItemDto)
        .pipe(this.takeUntilDestroy())
        .subscribe({
          next: (data) => {
            this.onClose(data);
          },
          error: () => {
            this.apNotificationService.error('Your change could not be saved due to an unknown error.', 'Something went wrong.');
            this.isSaving = false;
          },
        });
    } else {
      this.findControlRepository
        .createItem<FcModifyScheduledReportItemDto>(this.fcModalItemContext.findControlId, modifyItemDto)
        .pipe(this.takeUntilDestroy())
        .subscribe({
          next: (data) => {
            this.onClose(data);
          },
          error: () => {
            this.apNotificationService.error('Your new item could not be saved due to an unknown error.', 'Something went wrong.');
            this.isSaving = false;
          },
        });
    }
  }

  // Report populate handler removed; selection handled via property setter.
  public onReportChipsChange(chips: IColumnPk[]): void {
    const first = chips && chips.length ? chips[0] : null;
    if (first) {
      // Ensure selectedReportUid setter runs (chips array might already match, but enforce for clarity)
      this.selectedReportUid = [first.Pk];
      const uiConfigUid = (first as any).UIConfigurationUid; // dynamic property from find control row
      if (uiConfigUid) {
        this.uiFilterTypeAdditionalSearchParameters = {
          Type: FindControlAdditionalSearchParametersType.Standard,
          AdditionalSearchParameters: [
            { ColumnName: 'UIConfigurationUid', Value: uiConfigUid }
          ]
        } as FindControlAdditionalSearchParametersDto;
        this.disableFilter = false;
      } else {
        this.disableFilter = true;
      }
    } else {
      this.selectedReportUid = [];
      this.disableFilter = true;
      // Optionally clear additional parameters
      this.uiFilterTypeAdditionalSearchParameters = {
        Type: FindControlAdditionalSearchParametersType.Standard,
        AdditionalSearchParameters: []
      } as FindControlAdditionalSearchParametersDto;
    }
  }

  // Filter and JobScheduleTimeBase chipsChange handlers removed - property setters handle state + dirty marking.

  private markDirty(control: AbstractControl): void {
    control.markAsDirty();
    control.markAsTouched();
    this.form.markAsDirty();
  }

  private arraysEqual(a: string[] | null | undefined, b: string[] | null | undefined): boolean {
    if (a === b) { return true; }
    if (!a || !b) { return !a && !b; }
    if (a.length !== b.length) { return false; }
    for (let i = 0; i < a.length; i++) {
      if (a[i] !== b[i]) { return false; }
    }
    return true;
  }

  /**
   * Central helper applying configured validators or fallback required validator.
   * @param control target form control
   * @param configKey validation key in fcListValidation.validations
   * @param isArray treat empty array as invalid when required
   */
  private applyRequiredValidator(control: AbstractControl, configKey: string, isArray = false): void {
    if (!this.fcListValidation) { return; }
    const configured = this.fcListValidation.getValidators(configKey);
    if (configured && configured.length) {
      control.setValidators(configured);
      control.updateValueAndValidity({ emitEvent: false });
      return;
    }
    const meta: any = (this.fcListValidation as any).validations?.[configKey];
    if (meta?.IsRequired) {
      control.setValidators([(ctrl) => {
        const v = ctrl.value;
        if (isArray) { return (v && Array.isArray(v) && v.length) ? null : { required: true }; }
        return v ? null : { required: true };
      }]);
      control.updateValueAndValidity({ emitEvent: false });
    }
  }

  // Removed individual recipients handlers; setter now handles dirty marking

  private getDate(rawDateTime: string): string
  {
    return this.datePipe.transform(rawDateTime, 'M/d/yyyy') || '';
  }

  private getTime(rawDateTime: string): string
  {
    return this.datePipe.transform(rawDateTime, 'HH:mm') || '';
  }

  private setDateTime(rawDate: Date, rawTime: string): string
  {
    let rawDateTime;
    if (rawDate instanceof Date)
    {
      rawDateTime = new Date(rawDate.toDateString() + ' ' + rawTime);
      return this.datePipe.transform(rawDateTime, 'M/d/yyyy HH:mm') || '';
    }
    if (rawDate === '')
    {
      return '';
    }
    else
    {
      rawDateTime = new Date(rawDate + ' ' + rawTime);
      return this.datePipe.transform(rawDateTime, 'M/d/yyyy HH:mm') || '';
    }
  }

  private resetRepeatIntervalControls()
  {
    this.timeBaseNthSecondControl.setValue('');
    this.timeBaseNthMinuteControl.setValue('');
    this.timeBaseHourControl.setValue('');
    this.timeBaseDailyTypeControl.setValue(1);
    this.timeBaseDailyTimeControl.setValue('');
    this.timeBaseWeeklyTimeControl.setValue('');
    this.timeBaseWeeklySundayControl.setValue(false);
    this.timeBaseWeeklyMondayControl.setValue(false);
    this.timeBaseWeeklyTuesdayControl.setValue(false);
    this.timeBaseWeeklyWednesdayControl.setValue(false);
    this.timeBaseWeeklyThursdayControl.setValue(false);
    this.timeBaseWeeklyFridayControl.setValue(false);
    this.timeBaseWeeklySaturdayControl.setValue(false);
    this.timeBaseMonthlyDayOfMonthControl.setValue('');
    this.timeBaseMonthlyTimeControl.setValue('');
    // After clearing, also reflect disabled state for weekly day checkboxes
    this.updateWeeklyDayControlsDisabledState();
  }

  private updateWeeklyDayControlsDisabledState(): void {
    const hasWeeklyTime = !!this.timeBaseWeeklyTimeControl.value;
    const dayControls = [
      this.timeBaseWeeklySundayControl,
      this.timeBaseWeeklyMondayControl,
      this.timeBaseWeeklyTuesdayControl,
      this.timeBaseWeeklyWednesdayControl,
      this.timeBaseWeeklyThursdayControl,
      this.timeBaseWeeklyFridayControl,
      this.timeBaseWeeklySaturdayControl,
    ];
    dayControls.forEach(ctrl => {
      if (hasWeeklyTime) {
        if (ctrl.disabled) { ctrl.enable({ emitEvent: false }); }
      } else {
        // Clear any previously selected day (do not emit events to avoid extra dirty churn; the time field change already marks dirty)
        if (ctrl.value) { ctrl.setValue(false, { emitEvent: false }); }
        if (ctrl.enabled) { ctrl.disable({ emitEvent: false }); }
      }
    });
  }
}
